// appConfig/attendee: the one document a phone reads before it knows which event it is in.
//
// Why this module exists at all (docs/NATIVE-APPS-PLAN.md §6 items 1, 2, 4 and 5). Everything here is
// pure and every guard takes `unknown`, because this document is read by a store binary that may be
// months older than the value it finds (plan §5 rule 7) and that cannot be patched for weeks. There is
// no zod: this module is inside the `@spark/shared/app` door and Metro does not tree-shake, so a zod
// import would be bundled AND constructed at launch — packages/shared/test/app-door.test.ts asserts
// the whole graph has no bare specifiers at all.
//
// The deliberate omission is a schema version. This document's compatibility negotiation is `minBuild`,
// running the other way; per-field fallback below degrades better than a version number, because one
// bad field cannot condemn the rest. `serviceMessage` is therefore a bare string forever: an urgency
// flag lands as a SEPARATE field, never by widening this one — an old binary ignores a field it has
// never heard of, but a map where it expects a string is a rendering bug on a phone nobody can patch.
//
// This module must not import `paths.ts`: paths.ts imports APP_CONFIG_DOC from here, the way it already
// imports LIVE_SUMMARY_DOC from live.ts, and the edge may only run one way.
import { SAFE_ID } from './audience.ts'
import type { TimestampLike } from './types.ts'

/** The one document in `appConfig`. paths.ts imports this id; see live.ts for the same shape. */
export const APP_CONFIG_DOC = 'attendee'

/**
 * How long the app may serve a cached pointer before asking again, and its `staleAfterMs`.
 *
 * This is a ceiling on cache REUSE, not a poll interval: `loadDoc` re-reads only when something calls
 * it. What turns it into an actual bound on how long a `serviceMessage` takes to reach a phone is the
 * foreground interval in the native provider — without that, a phone left open on one screen would
 * never re-read at all, and §6 item 5's "the only in-event control that cannot fail silently" would do
 * exactly that. The read budget it buys is one `get` per this interval per FOREGROUNDED phone.
 *
 * Ten minutes is the floor the read model already permits: read-model.test.ts floors every TTL there,
 * and `quranOrder` already sits at it. A pull to refresh passes `force` and pays one read to skip it.
 *
 * Since the live summary carries `pointerAt` (`pointerStampMs` below), an open phone re-reads within
 * seconds of a save; this stays the ceiling where no summary is heard (see the native provider).
 */
export const APP_CONFIG_TTL_MS = 10 * 60_000

/** Longest `serviceMessage` the rules accept. Mirrors `str(incoming().serviceMessage, 200)`. */
export const SERVICE_MESSAGE_MAX = 200

/**
 * Longest store URL the rules accept. Mirrors `str(v, 300)` in appleLink/playLink.
 *
 * Exported because THREE places enforce it and they must agree: the rules, the guard below, and
 * `scripts/app-pointer.mjs`. A script that accepted a longer value would write a document the rules
 * then refuse to update — which locks the service message, the one in-event lever, until someone
 * re-runs the script. A referral or tracking parameter on a Play URL passes 300 easily.
 */
export const STORE_URL_MAX = 300

/** A build floor of 0 means "no gate". The default for both floors, and what an unparseable one becomes. */
export const NO_BUILD_GATE = 0

/**
 * The only hosts a store link may point at.
 *
 * Any `https://` would have been the loose choice; this file's own precedent is the opposite —
 * `safeImageUrl` in firestore.rules allow-lists the three hosts an image may come from, and an image
 * is far less dangerous than the link a blocking "Update required" screen tells every attendee they
 * must tap. Enforced in BOTH the rules and `storeUrl()` below, so a binary already on a phone rejects
 * a bad value even if the rules were widened after it shipped.
 */
export const STORE_URL_PREFIX: Readonly<Record<AppPlatform, string>> = {
  ios: 'https://apps.apple.com/',
  android: 'https://play.google.com/',
}

export type AppPlatform = 'ios' | 'android'

export interface BuildFloors {
  ios: number
  android: number
}

export interface StoreUrls {
  ios: string
  android: string
}

/** appConfig/attendee: the native app's control plane. Public get; written by staff (§6 item 1). */
export interface AppConfigDoc {
  /** The event every phone should read. Always a SAFE_ID; never contains a path separator. */
  currentEventId: string
  /** Below this build, a dismissible "Update available" banner. 0 disables it. */
  minBuild: BuildFloors
  /** Below this build the data is incompatible and the app blocks. 0 disables it, and 0 is the default. */
  blockBelowBuild: BuildFloors
  storeUrls: StoreUrls
  /** One staff-editable line shown on Home. Empty string = nothing to say. */
  serviceMessage: string
  updatedAt: TimestampLike | null
  /**
   * The uid that last wrote it. The rules pin it to request.auth.uid: a top-level document gets no
   * audit trail (maintenance.ts watches only events/{e}/…), so this field IS the attribution.
   */
  updatedBy: string | null
}

/**
 * What a screen may rely on: every field present, every value already guarded, no timestamps. This is
 * what `appPointerFrom` returns and what every consumer takes — nothing downstream ever sees the raw
 * document.
 */
export interface AppPointer {
  currentEventId: string
  minBuild: BuildFloors
  blockBelowBuild: BuildFloors
  storeUrls: StoreUrls
  serviceMessage: string
}

/**
 * The document's `updatedAt` in epoch ms, or null when it has none a phone could compare.
 *
 * This is the value the live summary carries as `pointerAt` (live.ts): the functions read it from the
 * document with the Admin SDK and the phone reads it from the same document with its own SDK, so both
 * sides are the SERVER's timestamp of the same write and a phone's clock never enters the comparison.
 * Takes `unknown` for the same reason every guard here does, and accepts either SDK's Timestamp (a
 * `toMillis()`) or the plain `{ seconds, nanoseconds }` a serialised one becomes.
 */
export function pointerStampMs(doc: unknown): number | null {
  if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) return null
  const at = (doc as Record<string, unknown>).updatedAt
  if (typeof at !== 'object' || at === null) return null
  const t = at as { toMillis?: unknown; seconds?: unknown; nanoseconds?: unknown }
  const ms =
    typeof t.toMillis === 'function'
      ? (t.toMillis as () => unknown)()
      : typeof t.seconds === 'number'
        ? t.seconds * 1000 + Math.floor((typeof t.nanoseconds === 'number' ? t.nanoseconds : 0) / 1e6)
        : null
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : null
}

const floor = (v: unknown, fb: number): number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 1_000_000 ? v : fb

const floors = (v: unknown, fb: BuildFloors): BuildFloors => {
  if (typeof v !== 'object' || v === null) return fb
  const m = v as Record<string, unknown>
  return { ios: floor(m.ios, fb.ios), android: floor(m.android, fb.android) }
}

/**
 * '' or a real store URL for that platform. Anything else is the previous value: this is the link the
 * blocking screen sends an attendee to, so a wrong host is worse than no link at all — and no link is
 * itself handled, by `updateGate` refusing to block without one.
 */
const storeUrl = (v: unknown, fb: string, platform: AppPlatform): string =>
  typeof v === 'string' && v.length <= STORE_URL_MAX && (v === '' || v.startsWith(STORE_URL_PREFIX[platform])) ? v : fb

const urls = (v: unknown, fb: StoreUrls): StoreUrls => {
  if (typeof v !== 'object' || v === null) return fb
  const m = v as Record<string, unknown>
  return { ios: storeUrl(m.ios, fb.ios, 'ios'), android: storeUrl(m.android, fb.android, 'android') }
}

/**
 * The pointer a binary has before it has spoken to the server. `eventId` is whatever the build baked
 * in (mobile/app.config.ts's `extra.fallbackEventId`, which may be absent after an OTA precomputes the
 * manifest); `defaultEventId` is DEFAULT_EVENT_ID, passed in rather than imported because paths.ts
 * imports THIS module and the edge may only run one way.
 */
export function bundledPointer(eventId: unknown, defaultEventId: string): AppPointer {
  return {
    currentEventId: typeof eventId === 'string' && SAFE_ID.test(eventId) ? eventId : defaultEventId,
    minBuild: { ios: NO_BUILD_GATE, android: NO_BUILD_GATE },
    blockBelowBuild: { ios: NO_BUILD_GATE, android: NO_BUILD_GATE },
    storeUrls: { ios: '', android: '' },
    serviceMessage: '',
  }
}

/**
 * The document as read, made safe, field by field. `fallback` is what the app is using now — the
 * bundled pointer on a cold start, the last good pointer afterwards — so ONE bad field costs only that
 * field. `currentEventId` is checked against SAFE_ID because it becomes a Firestore path segment AND an
 * MMKV key prefix: `eventDoc()` (paths.ts) interpolates it raw, so an id containing '/' would silently
 * produce a path of a different depth.
 */
export function appPointerFrom(v: unknown, fallback: AppPointer): AppPointer {
  // An array is `typeof 'object'` too, and treating one as "a document with every field absent"
  // would let a garbage read CLEAR a live service message — the one field that does not fall back.
  // Not a document at all means keep everything, which is what `fallback` is.
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return fallback
  const d = v as Record<string, unknown>
  const id = d.currentEventId
  return {
    currentEventId: typeof id === 'string' && SAFE_ID.test(id) ? id : fallback.currentEventId,
    minBuild: floors(d.minBuild, fallback.minBuild),
    blockBelowBuild: floors(d.blockBelowBuild, fallback.blockBelowBuild),
    storeUrls: urls(d.storeUrls, fallback.storeUrls),
    // Not `fallback.serviceMessage`: a message that has been CLEARED must clear, and anything that is
    // not a string (a future map, a number) must show nothing rather than "[object Object]".
    serviceMessage: typeof d.serviceMessage === 'string' ? d.serviceMessage.slice(0, SERVICE_MESSAGE_MAX) : '',
  }
}
